import React, { useState } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { DatePicker } from "antd";
import moment from "moment";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import { gbState } from "../../../components/shared/GlobalState";
import {
  FaCalendarAlt,
  FaUser,
  FaBriefcase,
  FaBirthdayCake,
  FaMapMarkerAlt,
  FaUserMd,
  FaComments,
} from "react-icons/fa";

const AddBurialChild = () => {
  let navigate = useNavigate();
  const style = {
    control: (base) => ({
      ...base,
      border: "1px solid #e2e8f0",
      borderRadius: "0.375rem",
      boxShadow: "none",
      "&:hover": {
        border: "1px solid #4299e1",
      },
    }),
  };

  const [{ errorLogApi }] = useAtom(gbState);
  const [formData, setFormData] = useState({
    date_of_demise: null,
    deceased_name: "",
    age: "",
    profession: "",
    date_of_birth: null,
    location_of_burial: "",
    burial_done_by: "",
    remarks: "",
  });

  const handleChange = ({ currentTarget: input }) => {
    setFormData({
      ...formData,
      [input.name]: input.value,
    });
  };

  function formatString(str) {
    // Remove underscores and split the string into words
    const words = str.replace(/_/g, " ").split(" ");

    // Capitalize the first letter of the first word
    const capitalizedWords = words.map((word, index) => {
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      return word;
    });

    // Join the words back into a single string
    return capitalizedWords.join(" ");
  }

  const handleBurialRegister = () => {
    const fieldToValidate = [
      "date_of_demise",
      "deceased_name",
      "profession",
      "date_of_birth",
      "location_of_burial",
      "burial_done_by",
    ];

    // Identify empty fields and filter out any undefined entries
    const emptyFields = fieldToValidate
      .filter((f) => !formData[f])
      .map(formatString); // Assuming formatString formats the string as previously discussed

    // Provide feedback if there are any empty fields
    if (emptyFields.length > 0) {
      return toast.error(`${emptyFields.join(", ")} cannot be empty`);
    }

    // Configuration for axios request
    let userToken = localStorage.getItem("token");
    const config = {
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/burial/add`,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
      data: {
        ...formData,
      },
    };

    // Axios request
    axios(config)
      .then(function (response) {
        toast.success(response?.data?.message);
        navigate("/burial/list");
      })
      .catch(function (error) {
        toast.error(
          error.response?.data
            ? JSON.stringify(error.response.data)
            : "An error occurred"
        );
        errorLogApi(error, "AddBurial", "/burial/add");
      });
  };

  return (
    <div className="max-w-4xl p-6 mx-auto bg-white rounded-lg shadow-lg">
      <h2 className="mb-6 text-2xl font-semibold text-gray-800">
        Add Burial Record
      </h2>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="space-y-4">
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Date of Demise
            </label>
            <DatePicker
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              name="date_of_demise"
              format="MM-DD-YYYY"
              onChange={(date, dateString) =>
                setFormData({ ...formData, date_of_demise: dateString })
              }
              disabledDate={(current) =>
                current && current > dayjs().endOf("day")
              }
              suffixIcon={<FaCalendarAlt className="text-gray-400" />}
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Deceased Name
            </label>
            <div className="relative">
              <input
                className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                name="deceased_name"
                type="text"
                required
                placeholder="Deceased Name"
                onChange={handleChange}
                value={formData.deceased_name}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaUser className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Age
            </label>
            <div className="relative">
              <input
                className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                name="age"
                type="text"
                required
                placeholder="Age"
                onChange={handleChange}
                value={formData.age}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaBirthdayCake className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Profession
            </label>
            <div className="relative">
              <input
                className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                name="profession"
                type="text"
                required
                placeholder="Profession"
                onChange={handleChange}
                value={formData.profession}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaBriefcase className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Date of Birth
            </label>
            <DatePicker
              className="w-full border-gray-300 rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              name="date_of_birth"
              format="MM-DD-YYYY"
              onChange={(date, dateString) =>
                setFormData({ ...formData, date_of_birth: dateString })
              }
              disabledDate={(current) =>
                current && current > moment().endOf("day")
              }
              suffixIcon={<FaBirthdayCake className="text-gray-400" />}
            />
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Location of Burial
            </label>
            <div className="relative">
              <input
                className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                name="location_of_burial"
                type="text"
                required
                placeholder="Location"
                onChange={handleChange}
                value={formData.location_of_burial}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaMapMarkerAlt className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">
              Burial done by
            </label>
            <div className="relative">
              <input
                className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                name="burial_done_by"
                type="text"
                required
                placeholder="Burial Done by"
                onChange={handleChange}
                value={formData.burial_done_by}
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <FaUserMd className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6">
        <label className="block mb-1 text-sm font-medium text-gray-700">
          Remarks
        </label>
        <div className="relative">
          <textarea
            className="w-full py-2 pl-10 pr-3 leading-5 placeholder-gray-500 bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            name="remarks"
            rows={3}
            placeholder="Additional remarks"
            onChange={handleChange}
            value={formData.remarks}
          />
          <div className="absolute pointer-events-none top-3 left-3">
            <FaComments className="w-5 h-5 text-gray-400" />
          </div>
        </div>
      </div>
      <div className="flex justify-end mt-8 space-x-4">
        <button
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          onClick={() => navigate(-1)}
        >
          Back
        </button>
        <button
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          onClick={handleBurialRegister}
        >
          Create Record
        </button>
      </div>
    </div>
  );
};

export default AddBurialChild;
